import { z } from 'zod';
import { timestampSchema } from './common.schemas';

/**
 * Integrations & Webhooks Schemas
 */

// Integration types
export const integrationTypeEnum = z.enum(['salesforce', 'slack', 'zendesk']);

// Integration schema
export const integrationSchema = z
  .object({
    id: z.string().uuid(),
    type: integrationTypeEnum,
    name: z.string(),
    status: z.enum(['connected', 'disconnected', 'error']),
    lastSyncAt: z.coerce.date().nullable(),
    syncStatus: z.enum(['success', 'failed', 'in_progress']).nullable(),
    syncError: z.string().nullable(),
    organizationId: z.string().uuid(),
  })
  .merge(timestampSchema);

// Create integration
export const createIntegrationSchema = z.object({
  type: integrationTypeEnum,
  name: z.string().min(1).max(100),
  config: z.record(z.unknown()),
  credentials: z.record(z.unknown()),
});

// Update integration
export const updateIntegrationSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  config: z.record(z.unknown()).optional(),
  credentials: z.record(z.unknown()).optional(),
});

// Webhook schema
export const webhookSchema = z
  .object({
    id: z.string().uuid(),
    url: z.string().url(),
    events: z.array(z.string()),
    status: z.enum(['active', 'paused', 'failed']),
    lastTriggered: z.coerce.date().nullable(),
    failureCount: z.number().int().nonnegative(),
    organizationId: z.string().uuid(),
  })
  .merge(timestampSchema);

// Create webhook
export const createWebhookSchema = z.object({
  url: z.string().url(),
  events: z
    .array(
      z.enum([
        'conversation.created',
        'conversation.resolved',
        'conversation.escalated',
        'escalation.created',
        'escalation.assigned',
        'escalation.resolved',
        'agent.action.completed',
      ]),
    )
    .min(1),
});

// Update webhook
export const updateWebhookSchema = z.object({
  url: z.string().url().optional(),
  events: z.array(z.string()).optional(),
  status: z.enum(['active', 'paused', 'failed']).optional(),
});

// Webhook delivery
export const webhookDeliverySchema = z.object({
  id: z.string().uuid(),
  event: z.string(),
  status: z.enum(['pending', 'delivered', 'failed']),
  statusCode: z.number().int().nullable(),
  response: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  nextRetry: z.coerce.date().nullable(),
  deliveredAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  webhookId: z.string().uuid(),
});

// Test webhook
export const testWebhookSchema = z.object({
  event: z.string(),
  payload: z.record(z.unknown()),
});

// Export inferred types
export type IntegrationType = z.infer<typeof integrationTypeEnum>;
export type Integration = z.infer<typeof integrationSchema>;
export type CreateIntegrationInput = z.infer<typeof createIntegrationSchema>;
export type UpdateIntegrationInput = z.infer<typeof updateIntegrationSchema>;
export type Webhook = z.infer<typeof webhookSchema>;
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;
export type WebhookDelivery = z.infer<typeof webhookDeliverySchema>;
export type TestWebhookInput = z.infer<typeof testWebhookSchema>;
